import { Component } from '@theme/component';

/**
 * TicketCartManager handles cart clearing logic when switching between cities
 * Supports two strategies:
 * - on_load: Clear cart on page load if product mismatch detected
 * - on_add: Clear cart when adding tickets from a different city (with confirmation)
 *
 * @extends {Component}
 */
class TicketCartManager extends Component {
  static observedAttributes = ['data-product-id', 'data-strategy', 'data-addon-product-ids', 'data-event-handle'];

  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.strategy = this.dataset.strategy || 'on_add';
    this.addonProductIds = (this.dataset.addonProductIds || '').split(',').filter(Boolean);
    this.eventHandle = this.dataset.eventHandle || '';
    this.isProcessing = false;
    this.boundHandleBeforeAdd = this.handleBeforeAdd.bind(this);
    this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
    this.isRemovingOrphans = false;
  }

  async connectedCallback() {
    document.addEventListener('cart:update', this.boundHandleCartUpdate);

    if (this.strategy === 'on_add') {
      this.setupAddToCartInterceptor();
    }

    await this.removeOrphanedAddons();

    if (this.strategy === 'on_load') {
      await this.checkAndClearOnLoad();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.boundHandleCartUpdate);
    if (this.strategy === 'on_add') {
      document.removeEventListener('cart:before-add', this.boundHandleBeforeAdd);
    }
  }

  /**
   * Determine whether a cart line conflicts with the current city.
   * The city's ticket product, its allowlisted addon products, and addon
   * lines tagged with this city's handle are never conflicts.
   * @param {Object} item - Cart line item from /cart.js
   * @returns {boolean}
   */
  isConflictingItem(item) {
    if (!item.product_id) return false;
    if (String(item.product_id) === String(this.productId)) return false;

    const props = item.properties || {};
    if (props._addon === 'true' && props._city) {
      return props._city !== this.eventHandle;
    }
    if (this.addonProductIds.includes(String(item.product_id))) return false;

    return true;
  }

  /**
   * Strategy 1: Check cart on page load and clear if product mismatch
   * @returns {Promise<void>}
   */
  async checkAndClearOnLoad() {
    try {
      const cart = await this.fetchCart();

      if (!cart || !cart.items || cart.items.length === 0) {
        return;
      }

      const hasDifferentProduct = cart.items.some((item) => this.isConflictingItem(item));

      if (hasDifferentProduct) {
        await this.clearCart();
        this.showNotification(this.dataset.notificationClearedCity || 'Cart cleared: You switched to a different city.');
      }
    } catch (error) {
      console.error('[TicketCartManager] Error checking cart on load:', error);
    }
  }

  /**
   * Strategy 2: Intercept cart additions and check for product mismatch
   */
  setupAddToCartInterceptor() {
    document.addEventListener('cart:before-add', this.boundHandleBeforeAdd);
  }

  /**
   * Handle cart:before-add event
   * @param {CustomEvent} event
   */
  async handleBeforeAdd(event) {
    // Must preventDefault synchronously before any await (and before any
    // early return), otherwise a second click while we're already
    // processing — e.g. the city-conflict confirm modal is still open —
    // posts straight through to /cart/add.js instead of being held here.
    event.preventDefault();

    if (this.isProcessing) return;

    const { items } = event.detail || {};
    if (!items || items.length === 0) return;

    this.isProcessing = true;

    try {
      const cart = await this.fetchCart();

      if (!cart || !cart.items || cart.items.length === 0) {
        // No cart conflict — dispatch add-after-clear to proceed with the add
        document.dispatchEvent(new CustomEvent('cart:add-after-clear', {
          detail: event.detail
        }));
        return;
      }

      const hasDifferentProduct = cart.items.some((item) => this.isConflictingItem(item));

      if (hasDifferentProduct) {
        const confirmed = await this.showConfirmationModal();

        if (confirmed) {
          await this.clearCart();
          this.showNotification(this.dataset.notificationClearedPrevious || 'Cart cleared: Previous city tickets removed.');
        } else {
          // User cancelled — don't add
          return;
        }
      }

      // Either no conflict or cart was cleared — proceed with add
      document.dispatchEvent(new CustomEvent('cart:add-after-clear', {
        detail: event.detail
      }));
    } catch (error) {
      console.error('[TicketCartManager] Error intercepting cart add:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Fetch current cart state
   * @returns {Promise<Object>}
   */
  async fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    return response.json();
  }

  /**
   * Clear all items from cart
   * @returns {Promise<void>}
   */
  async clearCart() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to clear cart');
    }

    // Dispatch cart update event
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: await response.json(),
        sourceId: 'ticket-cart-manager',
        data: {
          source: 'ticket-cart-manager',
          action: 'clear'
        }
      }
    }));
  }

  /**
   * Show confirmation modal for cart clearing
   * @returns {Promise<boolean>}
   */
  showConfirmationModal() {
    return new Promise((resolve) => {
      // Remove any existing modal to prevent stacking
      const existingModal = document.querySelector('ticket-cart-confirm-modal');
      if (existingModal) {
        existingModal.remove();
      }

      const modal = document.createElement('ticket-cart-confirm-modal');
      modal.dataset.message = this.dataset.modalMessage || 'You have tickets from a different city in your cart. Clear cart and add these tickets?';

      const handleConfirm = () => {
        modal.remove();
        modal.removeEventListener('confirm', handleConfirm);
        modal.removeEventListener('cancel', handleCancel);
        resolve(true);
      };

      const handleCancel = () => {
        modal.remove();
        modal.removeEventListener('confirm', handleConfirm);
        modal.removeEventListener('cancel', handleCancel);
        resolve(false);
      };

      modal.addEventListener('confirm', handleConfirm);
      modal.addEventListener('cancel', handleCancel);

      document.body.appendChild(modal);
    });
  }

  /**
   * Show notification to user
   * @param {string} message
   */
  showNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'ticket-cart-notification';
    notification.textContent = message;
    notification.setAttribute('role', 'alert');

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('ticket-cart-notification--visible');
    }, 10);

    setTimeout(() => {
      notification.classList.remove('ticket-cart-notification--visible');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * A cart with add-on lines but no ticket lines cannot be attributed to an
   * event by the backend — remove the add-ons and tell the customer.
   * @param {CustomEvent} [event]
   */
  async handleCartUpdate(event) {
    if (event?.detail?.data?.source === 'ticket-cart-manager-orphans') return;
    await this.removeOrphanedAddons();
  }

  /**
   * Remove add-on lines when no ticket lines exist in cart.
   * Re-entrancy guarded by isRemovingOrphans flag to prevent concurrent removals.
   * @returns {Promise<void>}
   */
  async removeOrphanedAddons() {
    if (this.isRemovingOrphans || this.isProcessing) return;
    this.isRemovingOrphans = true;

    try {
      const cart = await this.fetchCart();
      const items = cart?.items || [];
      if (items.length === 0) return;

      const hasTickets = items.some(
        (item) =>
          item.properties?._addon !== 'true' &&
          (item.properties?._ticket === 'true' ||
            item.product_type === 'Ticket' ||
            String(item.product_id) === String(this.productId))
      );
      if (hasTickets) return;

      const addonLines = items.filter(
        (item) => item.properties?._addon === 'true' || item.product_type === 'Add-On'
      );
      if (addonLines.length === 0) return;

      const updates = {};
      addonLines.forEach((item) => {
        updates[item.key] = 0;
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Failed to remove orphaned add-ons');
      }

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: await response.json(),
          sourceId: 'ticket-cart-manager',
          data: {
            source: 'ticket-cart-manager-orphans',
            action: 'remove-orphaned-addons'
          }
        }
      }));

      this.showNotification(
        this.dataset.notificationAddonsRemoved || "Add-ons removed — they're only available with event tickets."
      );
    } catch (error) {
      console.error('[TicketCartManager] Error removing orphaned add-ons:', error);
    } finally {
      this.isRemovingOrphans = false;
    }
  }
}

customElements.define('ticket-cart-manager', TicketCartManager);
